import type React from "react";
import { useEffect } from "react";
import { captureAttribution } from "./lib/gtm";
import { createBrowserRouter, Outlet, useLocation, ScrollRestoration } from "react-router";
import { PublicLayout } from "./components/layout/PublicLayout";
import { AppShell } from "./components/layout/AppShell";
import { Home, NotFound } from "./pages/public/Home";
import { Login, Signup } from "./pages/public/Auth";
import { Marketplace, MarketplaceDataset } from "./pages/public/Marketplace";
import { Docs } from "./pages/public/Docs";
import { Pricing } from "./pages/public/Pricing";
import { Security } from "./pages/public/Security";


type Mod = Record<string, unknown>;
/** Code-split authenticated app pages; the public site stays in the main chunk. */
const page = (load: () => Promise<Mod>, name: string) => async () => ({ Component: (await load())[name] as React.ComponentType });
const L_sources = () => import("./pages/provider/Sources");
const L_wizard = () => import("./pages/provider/SourceWizard");
const L_quick = () => import("./pages/provider/QuickConnect");
const L_ask = () => import("./pages/provider/Ask");
const L_datasets = () => import("./pages/provider/Datasets");
const L_mapping = () => import("./pages/provider/DatasetMapping");
const L_econ = () => import("./pages/provider/Economics");
const L_ops = () => import("./pages/provider/Operations");
const L_dev = () => import("./pages/consumer/Developers");
const L_consumer = () => import("./pages/consumer/Consumer");
const L_gtm = () => import("./pages/gtm/Public");
const L_internal = () => import("./pages/internal/Internal");
const L_growth = () => import("./pages/growth/Growth");

function Root() {
  const loc = useLocation();
  // First-touch attribution (utm_*, referrer host, ?partner=, ?ref=, ?invite=) — stored locally, sent only with signup.
  useEffect(() => { captureAttribution(); }, [loc.pathname]);
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { path: "/", element: <Home /> },
          { path: "/marketplace", element: <Marketplace /> },
          { path: "/marketplace/:dataset", element: <MarketplaceDataset /> },
          { path: "/docs", element: <Docs /> },
          { path: "/pricing", element: <Pricing /> },
          { path: "/security", element: <Security /> },
          // GTM: the network, demand exchange, developer + provider acquisition (docs/gtm)
          { path: "/developers", lazy: page(L_gtm, "DevelopersPage") },
          { path: "/partners/founding-data", lazy: page(L_gtm, "FoundingPartnersPage") },
          { path: "/discover", lazy: page(L_gtm, "PublicDiscoverPage") },
          { path: "/data-wanted", lazy: page(L_gtm, "DataWantedPage") },
          { path: "/opportunities/:slug", lazy: page(L_gtm, "OpportunityPage") },
          { path: "/data/:slug", lazy: page(L_gtm, "DataPage") },
          { path: "/integrations/mcp", lazy: page(L_gtm, "McpIntegrationPage") },
          { path: "/network", lazy: page(L_gtm, "NetworkPage") },
          { path: "*", element: <NotFound /> },
        ],
      },
      { path: "/login", element: <Login /> },
      { path: "/signup", element: <Signup /> },
      {
        element: <AppShell mode="provider" />,
        children: [
          { path: "/dashboard", lazy: page(L_econ, "ProviderDashboard") },
          { path: "/ask", lazy: page(L_ask, "AskPage") },
          { path: "/sources", lazy: page(L_sources, "Sources") },
          { path: "/sources/new", lazy: page(L_quick, "QuickConnect") },
          { path: "/sources/new/advanced", lazy: page(L_wizard, "SourceWizard") },
          { path: "/sources/:id", lazy: page(L_sources, "SourceDetail") },
          { path: "/sources/:id/schema", lazy: page(L_sources, "SourceSchema") },
          { path: "/sources/:id/exposure", lazy: page(L_sources, "SourceExposure") },
          { path: "/datasets", lazy: page(L_datasets, "Datasets") },
          { path: "/datasets/:id", lazy: page(L_datasets, "DatasetDetail") },
          { path: "/datasets/:id/mapping", lazy: page(L_mapping, "DatasetMapping") },
          { path: "/datasets/:id/pricing", lazy: page(L_datasets, "DatasetPricing") },
          { path: "/datasets/:id/queries", lazy: page(L_datasets, "DatasetQueries") },
          { path: "/architect", lazy: page(L_ops, "ArchitectHub") },
          { path: "/sync", lazy: page(L_ops, "Sync") },
          { path: "/discovery", lazy: page(L_ops, "Discovery") },
          { path: "/queries", lazy: page(L_econ, "ProviderQueries") },
          { path: "/revenue", lazy: page(L_econ, "Revenue") },
          { path: "/costs", lazy: page(L_econ, "Costs") },
          { path: "/pricing/manage", lazy: page(L_econ, "PricingManage") },
          { path: "/consumers", lazy: page(L_econ, "Consumers") },
          { path: "/opportunities", lazy: page(L_growth, "ProviderOpportunities") },
          { path: "/notifications", lazy: page(L_growth, "NotificationsPage") },
          { path: "/developers/api-keys", lazy: page(L_dev, "ApiKeys") },
          { path: "/developers/mcp", lazy: page(L_dev, "McpPage") },
          { path: "/security/center", lazy: page(L_ops, "SecurityCenter") },
          { path: "/audit", lazy: page(L_ops, "Audit") },
          { path: "/settings", lazy: page(L_ops, "Settings") },
          { path: "/settings/team", lazy: page(L_ops, "Team") },
        ],
      },
      {
        element: <AppShell mode="consumer" />,
        children: [
          { path: "/consumer", lazy: page(L_consumer, "ConsumerOverview") },
          { path: "/consumer/discover", lazy: page(L_consumer, "ConsumerDiscover") },
          { path: "/consumer/api-keys", lazy: page(L_dev, "ApiKeys") },
          { path: "/consumer/usage", lazy: page(L_consumer, "ConsumerUsage") },
          { path: "/consumer/costs", lazy: page(L_consumer, "ConsumerCosts") },
          { path: "/consumer/logs", lazy: page(L_consumer, "ConsumerLogs") },
          { path: "/consumer/budgets", lazy: page(L_consumer, "ConsumerBudgets") },
          { path: "/consumer/integrations", lazy: page(L_dev, "Integrations") },
          { path: "/consumer/team", lazy: page(L_ops, "Team") },
          { path: "/consumer/billing", lazy: page(L_consumer, "ConsumerBilling") },
          { path: "/consumer/waitlists", lazy: page(L_growth, "ConsumerWaitlists") },
          { path: "/consumer/notifications", lazy: page(L_growth, "NotificationsPage") },
        ],
      },
      {
        // Founders' command centre (§67). The server returns 404 for non-admins; the layout also guards client-side.
        path: "/internal",
        lazy: page(L_internal, "InternalLayout"),
        children: [
          { index: true, lazy: page(L_internal, "InternalOverview") },
          { path: "demand", lazy: page(L_internal, "InternalDemand") },
          { path: "demand/:id", lazy: page(L_internal, "InternalDemandDetail") },
          { path: "prospects", lazy: page(L_internal, "InternalProspects") },
          { path: "prospects/:id", lazy: page(L_internal, "InternalProspectDetail") },
          { path: "providers", lazy: page(L_internal, "InternalProviders") },
          { path: "developers", lazy: page(L_internal, "InternalDevelopers") },
          { path: "marketplace", lazy: page(L_internal, "InternalMarketplace") },
          { path: "queries", lazy: page(L_internal, "InternalQueries") },
          { path: "experiments", lazy: page(L_internal, "InternalExperiments") },
          { path: "revenue", lazy: page(L_internal, "InternalRevenue") },
          { path: "okrs", lazy: page(L_internal, "InternalOkrs") },
          { path: "report", lazy: page(L_internal, "InternalReport") },
          { path: "settings", lazy: page(L_internal, "InternalSettings") },
        ],
      },
    ],
  },
]);
